import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Chip,
  Autocomplete,
  Menu,
  Avatar,
  Tooltip,
  TableSortLabel,
  Dialog,
} from '@mui/material';
import {
  KeyboardArrowDown,
  CheckCircle,
  DoNotDisturbOn,
  ErrorOutline,
  InfoOutlined,
  ContentCopy,
  Visibility,
  MoreVert,
} from '@mui/icons-material';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import api from '../services/api';
import { listOtherExpenses, quickApproveOtherExpense, createTransferForOtherExpense, type OtherExpenseDto } from '../services/otherExpensesService';
import { getAllProjects } from '../services/projectService';
import { listUsersForBusinessForms, type AppUser } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../components/layouts/Layout';
import AddOtherExpenseDialog from '../components/invoices/AddOtherExpenseDialog';

const currency = (value: number) =>
  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 2 }).format(value);

function monthOptions(count = 18) {
  const now = new Date();
  const items: { key: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pl-PL', { year: 'numeric', month: 'long' });
    items.push({ key, label });
  }
  return items;
}

const SECTION_CHIPS = ['Faktury sprzedaży', 'Magazyny', 'Faktury zakupu', 'Inne wydatki', 'Wypłaty pracownicze', 'Rozrachunki', 'Operacje bankowe', 'Wyciągi i uzgodnienia', 'Eksport do Hermesu', 'Podatki i ZUS'];
const SECTION_SLUGS = ['sales', 'warehouses', 'purchases', 'other-expenses', 'employee-payouts', 'settlements', 'bank-operations', 'bank-statements', 'hermes-export', 'taxes-zus'];
const SECTION_COLORS = ['#B2DFDB', '#FFE0B2', '#FEEED5', '#FCE4EC', '#E8EAF6', '#E6F0FF', '#E6F5E6', '#FFF3E0', '#F3E5F5', '#DCEDC8'];
const SECTION_DISPLAY_ORDER = [0, 1, 2, 3, 4, 9, 5, 6, 7, 8];
const CURRENT_SECTION = 3;

const headerCellSx: any = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: '#FAFAFA',
  borderRight: '1px solid #d4d4d9',
  borderRadius: 0,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
};

const sortLabelBaseSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  color: '#2D2D32',
  display: 'inline-flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: '4px',
  '& .MuiTableSortLabel-icon': { opacity: 1, width: 12, height: 12, ml: '4px' }
} as const;
const ActiveSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
const getSortLabelProps = (isActive: boolean) => ({
  IconComponent: (isActive ? ActiveSortIcon : MutedSortIcon) as any,
  sx: { ...sortLabelBaseSx, '& .MuiTableSortLabel-icon': { ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'], color: isActive ? '#2D2D32' : '#d4d4d9' } }
}) as const;

const TAB_LABELS = ['Do akceptacji', 'Zaakceptowane', 'Nierozliczone', 'Rozliczone'];
const TAB_STATUSES = ['Do akceptacji', 'Zaakceptowane', 'Nierozliczone', 'Rozliczone'];

const OtherExpensesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  // Section chip menu
  const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  // Filters
  const [requesterFilter, setRequesterFilter] = React.useState('');
  const [projectFilter, setProjectFilter] = React.useState('');
  const [monthFilter, setMonthFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const months = React.useMemo(() => monthOptions(), []);

  // Tab
  const [tab, setTab] = React.useState(0);

  // Add dialog
  const [addOpen, setAddOpen] = React.useState(false);
  // Edit dialog (otwierany z menu "Edycja")
  const [editExpense, setEditExpense] = React.useState<OtherExpenseDto | null>(null);

  // Kebab menu
  const [menuRow, setMenuRow] = React.useState<OtherExpenseDto | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);

  // Preview
  const [previewExpense, setPreviewExpense] = React.useState<OtherExpenseDto | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);

  // Data
  const [rows, setRows] = React.useState<OtherExpenseDto[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Users & Projects
  const [usersMap, setUsersMap] = React.useState<Record<string, AppUser>>({});
  const [activeProjects, setActiveProjects] = React.useState<Array<{ id: string; name: string }>>([]);
  const STATIC_PROJECTS: Array<{ id: string; name: string }> = [
    { id: 'S14', name: 'SERWIS GWARANCYJNY: USŁUGI' },
    { id: 'S13', name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
    { id: 'S12', name: 'LEASINGI' },
    { id: 'S11', name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
    { id: 'S10', name: 'USŁUGI TELEINFORMATYCZNE' },
    { id: 'S9', name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
    { id: 'S8', name: 'NAJEM POWIERZCHNI BIUROWEJ' },
    { id: 'S7', name: 'PRACOWNICY B2B' },
    { id: 'S6', name: 'ZARZĄD' },
    { id: 'S5', name: 'USŁUGI WŁASNE' },
    { id: 'S4', name: 'MATERIAŁY WŁASNE' },
    { id: 'S3', name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
    { id: 'S2', name: 'WYPOSAŻENIE - do 3.000 zł' },
    { id: 'S1', name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
    { id: 'R11', name: 'PODATEK CIT' },
    { id: 'R10', name: 'PODATEK VAT' },
    { id: 'R9', name: 'ZUS' },
    { id: 'R8', name: 'PODATKI - PIT' },
    { id: 'R7', name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
    { id: 'R6', name: 'OBSŁUGA BANKOWA' },
    { id: 'R5', name: 'UBEZPIECZENIA I POLISY' },
    { id: 'R4', name: 'PRACOWNICY UoP, UD, UZ' },
    { id: 'R3', name: 'ZABEZPIECZENIE KONTRAKTÓW' },
    { id: 'R2', name: 'KOSZTY KREDYTÓW' },
    { id: 'R1', name: 'AMORTYZACJA' },
    { id: '.UN', name: 'USŁUGI NOCLEGOWE' },
    { id: '.UG', name: 'USŁUGI GASTRONOMICZNE' },
    { id: '.P2', name: 'POJAZDY - 50% ODLICZENIA VAT' },
    { id: '.P1', name: 'POJAZDY - 100% ODLICZENIA VAT' },
  ];

  // Sorting
  const [sortKey, setSortKey] = React.useState<string>('expense_date');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const handleSort = React.useCallback((key: string) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  // Load data
  const refresh = React.useCallback(() => {
    setLoading(true);
    listOtherExpenses()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  React.useEffect(() => {
    (async () => {
      try {
        const all = await getAllProjects();
        const active = all.filter((p: any) => String(p.project_phase).toUpperCase() === 'W REALIZACJI');
        const live = active.map((p: any) => ({ id: String(p.project_id), name: String(p.project_name || '') }));
        setActiveProjects([...live, ...STATIC_PROJECTS]);
      } catch { setActiveProjects([]); }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const allUsers = await listUsersForBusinessForms();
        const map: Record<string, AppUser> = {};
        allUsers.forEach((u: AppUser) => { map[u.user_id] = u; });
        setUsersMap(map);
      } catch {}
    })();
  }, []);

  // Pobierz PDF przez API (stream z konta serwisowego) – unika błędu uprawnień Google Drive
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
        setPreviewUrl(null);
      }
      setPreviewError(null);
      if (!previewExpense?.id || !previewExpense?.google_drive_file_id) {
        setPreviewLoading(false);
        return;
      }
      setPreviewLoading(true);
      try {
        const resp = await api.get(`/other-expenses/${previewExpense.id}/pdf`, { responseType: 'blob' });
        if (cancelled) return;
        const blob = resp.data as Blob;
        const objUrl = URL.createObjectURL(blob);
        setPreviewUrl(objUrl);
      } catch (e: any) {
        if (!cancelled) setPreviewError(e?.response?.data?.message || e?.message || 'Nie udało się pobrać PDF.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [previewExpense?.id, previewExpense?.google_drive_file_id]);

  // Option+ArrowDown – otwórz/zamknij menu sekcji (jak na InvoicesPage)
  const sectionChipRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'ArrowDown') {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        if (sectionMenuAnchorEl) {
          setSectionMenuAnchorEl(null);
        } else if (sectionChipRef.current) {
          setSectionMenuAnchorEl(sectionChipRef.current);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sectionMenuAnchorEl]);

  // Derive unique requesters from data
  const requesters = React.useMemo(() =>
    Array.from(new Set((rows || []).map((r) => String(r.created_by_name || '')).filter((x) => x && x !== 'undefined'))),
    [rows]
  );

  const ALL_STATUSES = React.useMemo(() => ['Do akceptacji', 'Zaakceptowane', 'Nierozliczone', 'Rozliczone', 'Pilny', 'Odrzucone'], []);

  // Filtered rows by filters (not by tab)
  const baseFiltered = React.useMemo(() => {
    return rows.filter((r) => {
      if (requesterFilter && String(r.created_by_name || '') !== requesterFilter) return false;
      if (projectFilter && r.project_id !== projectFilter) return false;
      if (monthFilter && r.expense_date) {
        const invoiceMonth = String(r.expense_date).slice(0, 7);
        if (invoiceMonth !== monthFilter) return false;
      }
      if (statusFilter && r.status !== statusFilter) return false;
      return true;
    });
  }, [rows, requesterFilter, projectFilter, monthFilter, statusFilter]);

  // Tab-filtered rows – use effective status derived from approvals
  const getTabStatus = React.useCallback((r: OtherExpenseDto) => {
    if (r.status === 'Nierozliczone' || r.status === 'Rozliczone') return r.status;
    const acc = String(r.accounting_approval_status || 'pending');
    const adm = String(r.admin_approval_status || 'pending');
    if (acc === 'approved' && adm === 'approved') return 'Zaakceptowane';
    if (acc === 'rejected' || adm === 'rejected') return 'Do akceptacji';
    return 'Do akceptacji';
  }, []);

  const tabFilteredRows = React.useMemo(() => {
    const tabStatus = TAB_STATUSES[tab];
    return baseFiltered.filter((r) => getTabStatus(r) === tabStatus);
  }, [baseFiltered, tab, getTabStatus]);

  // Tab counts
  const tabCounts = React.useMemo(() => {
    return TAB_STATUSES.map(s => baseFiltered.filter(r => getTabStatus(r) === s).length);
  }, [baseFiltered, getTabStatus]);

  // Sorted rows
  const sortedRows = React.useMemo(() => {
    const sorted = [...tabFilteredRows];
    sorted.sort((a: any, b: any) => {
      const valA = a[sortKey] ?? '';
      const valB = b[sortKey] ?? '';
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      const sA = String(valA).toLowerCase();
      const sB = String(valB).toLowerCase();
      return sortDir === 'asc' ? sA.localeCompare(sB) : sB.localeCompare(sA);
    });
    return sorted;
  }, [tabFilteredRows, sortKey, sortDir]);

  // Totals
  const totals = React.useMemo(() => {
    let gross = 0;
    sortedRows.forEach(r => {
      gross += Number(r.gross_amount) || 0;
    });
    return { gross };
  }, [sortedRows]);

  const handleSectionChange = (idx: number) => {
    const slug = SECTION_SLUGS[idx] || 'purchases';
    navigate(`/accounting/${slug}`, { replace: true });
    setSectionMenuAnchorEl(null);
  };

  // Approval helpers
  const getEffectiveStatus = React.useCallback((row: OtherExpenseDto): string => {
    const acc = String(row.accounting_approval_status || 'pending');
    const adm = String(row.admin_approval_status || 'pending');
    if (acc === 'approved' && adm === 'approved') return 'Zatwierdzone';
    if (acc === 'rejected' || adm === 'rejected') return 'Odrzucone';
    return 'Do akceptacji';
  }, []);

  const renderKebabMenu = () => (
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
      open={Boolean(menuPosition)}
      keepMounted
      onClose={() => { setMenuPosition(null); setMenuRow(null); }}
    >
      <MenuItem onClick={() => {
        if (menuRow) {
          setEditExpense(menuRow);
          setMenuPosition(null); setMenuRow(null);
        }
      }}>Edycja</MenuItem>
      <MenuItem onClick={() => { if (menuRow) window.open(`/accounting/other-expenses/approve/${menuRow.id}`, '_blank'); setMenuPosition(null); setMenuRow(null); }}>Okno zatwierdzenia</MenuItem>
      <MenuItem onClick={async () => {
        if (menuRow) {
          try {
            await quickApproveOtherExpense(menuRow.id, 'approved');
            showNotification('Wydatek zatwierdzony', 'success');
            refresh();
          } catch (err: any) {
            showNotification(err?.response?.data?.message || 'Błąd zatwierdzania', 'error');
          }
        }
        setMenuPosition(null); setMenuRow(null);
      }}>Zatwierdzam</MenuItem>
      <MenuItem onClick={async () => {
        if (menuRow) {
          try {
            await quickApproveOtherExpense(menuRow.id, 'rejected');
            showNotification('Wydatek odrzucony', 'success');
            refresh();
          } catch (err: any) {
            showNotification(err?.response?.data?.message || 'Błąd odrzucania', 'error');
          }
        }
        setMenuPosition(null); setMenuRow(null);
      }}>Odrzucam</MenuItem>
      {menuRow && !menuRow.transfer_invoice_id && (String(menuRow.admin_approval_status) === 'approved' || String(menuRow.accounting_approval_status) === 'approved') && (
        <MenuItem onClick={async () => {
          if (menuRow) {
            try {
              await createTransferForOtherExpense(menuRow.id);
              showNotification('Rozrachunek utworzony', 'success');
              refresh();
            } catch (err: any) {
              showNotification(err?.response?.data?.message || 'Błąd tworzenia rozrachunku', 'error');
            }
          }
          setMenuPosition(null); setMenuRow(null);
        }}>Utwórz rozrachunek</MenuItem>
      )}
    </Menu>
  );

  const tabSx = {
    textTransform: 'none' as const,
    color: '#020242',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    px: 3,
    minHeight: 32,
    height: 32,
    mt: '8px',
    bgcolor: '#F1F1F6',
    borderRadius: '4px 4px 0 0',
    '&.Mui-selected': {
      mt: 0,
      height: 40,
      bgcolor: '#E6E6EC',
      color: '#020242',
      fontWeight: 500,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' },
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek z chipem sekcji i przyciskiem */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40, mt: 0, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
          <Typography
            component="h1"
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '20px', lineHeight: '24px', fontWeight: 400, color: '#020242', m: 0 }}
          >
            Księgowość
          </Typography>
          <Box ref={sectionChipRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={SECTION_CHIPS[CURRENT_SECTION]}
              size="small"
              variant="filled"
              onClick={(e) => setSectionMenuAnchorEl(e.currentTarget)}
              sx={{
                cursor: 'pointer',
                bgcolor: SECTION_COLORS[CURRENT_SECTION],
                color: '#020242',
                borderRadius: '4px',
                minHeight: 28,
                height: 28,
                '& .MuiChip-label': {
                  px: 1, py: 0,
                  fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, lineHeight: '16px', color: '#020242'
                }
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setSectionMenuAnchorEl(e.currentTarget)}
              sx={{ p: 0.25, width: 24, height: 28, borderRadius: '4px', color: '#020242' }}
              aria-label="Wybierz sekcję"
            >
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={sectionMenuAnchorEl}
              open={Boolean(sectionMenuAnchorEl)}
              onClose={() => setSectionMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 1.5, minWidth: 180 } }}
            >
              {SECTION_DISPLAY_ORDER.map((idx) => (
                <MenuItem
                  key={SECTION_CHIPS[idx]}
                  selected={CURRENT_SECTION === idx}
                  onClick={() => handleSectionChange(idx)}
                  sx={{ gap: 1, '&.Mui-selected': { bgcolor: `${SECTION_COLORS[idx]}40` } }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SECTION_COLORS[idx], flexShrink: 0 }} />
                  {SECTION_CHIPS[idx]}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => setAddOpen(true)}
            data-action="add"
            sx={{
              minWidth: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' },
            }}
          >
            Dodaj wydatek
          </Button>
        </Box>
      </Box>

      {/* Filtry w stylu strony zakupów */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '300px 200px 200px 1fr',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA',
        }}
      >
        {/* Wnioskuje */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Wnioskuje</Typography>
          <Select
            size="small"
            value={requesterFilter}
            onChange={(e) => setRequesterFilter(String(e.target.value))}
            displayEmpty
            renderValue={requesterFilter !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps: any) => (
              <Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: requesterFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            }}
          >
            <MenuItem value="">Wszyscy</MenuItem>
            {requesters.map((name) => {
              const u = Object.values(usersMap).find(u => u.display_name === name);
              return (
                <MenuItem key={name} value={name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={u?.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                      {String(name || '?').substring(0, 1)}
                    </Avatar>
                    <span>{name}</span>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </Box>

        {/* Projekt */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Autocomplete
            size="small"
            options={activeProjects}
            getOptionLabel={(p) => `${p.id} ${p.name}`}
            value={activeProjects.find((p) => p.id === projectFilter) || null}
            onChange={(_, val) => setProjectFilter(val ? val.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Wybierz z listy"
                sx={{
                  '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px', pr: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: projectFilter ? '#2D2D32' : '#B9B9B9' },
                }}
              />
            )}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            clearOnBlur={false}
          />
        </Box>

        {/* Miesiąc */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Miesiąc</Typography>
          <Select
            size="small"
            value={monthFilter}
            onChange={(e) => setMonthFilter(String(e.target.value))}
            displayEmpty
            renderValue={monthFilter !== '' ? () => (months.find((m) => m.key === monthFilter)?.label || '') : () => 'Wybierz z listy'}
            IconComponent={(iconProps: any) => (
              <Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{
              height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: monthFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {months.map((m) => (<MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>))}
          </Select>
        </Box>

        {/* Status */}
        <Box sx={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(String(e.target.value))}
            displayEmpty
            renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps: any) => (
              <Box component="img" alt="dropdown" src="/assets/filters/keyboard_arrow_down.png" {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{
              width: '100%', height: 36, pr: '8px',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: 36, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: statusFilter ? '#2D2D32' : '#B9B9B9' },
              backgroundColor: '#fff', borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' }, '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {ALL_STATUSES.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </Select>
        </Box>
      </Box>

      {/* Zakładki tabeli */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="other-expenses tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' },
          }}
        >
          {TAB_LABELS.map((label, idx) => (
            <Tab
              key={label}
              disableRipple
              label={`${label} (${tabCounts[idx]})`}
              sx={tabSx}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        sx={{
          height: 'calc(100vh - 340px)',
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d0d0d6',
          borderBottom: '1px solid #d0d0d6',
          borderRadius: 0,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 },
        }}
      >
        <Table size="small" stickyHeader sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 0 rgba(224,224,224,1)', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 100 }}>
                <TableSortLabel active={sortKey === 'expense_date'} direction={sortKey === 'expense_date' ? sortDir : 'asc'} onClick={() => handleSort('expense_date')} {...getSortLabelProps(sortKey === 'expense_date')}>
                  Daty
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 60 }}>
                <TableSortLabel active={sortKey === 'created_by_name'} direction={sortKey === 'created_by_name' ? sortDir : 'asc'} onClick={() => handleSort('created_by_name')} {...getSortLabelProps(sortKey === 'created_by_name')}>
                  W
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 100 }}>
                <TableSortLabel active={sortKey === 'project_id'} direction={sortKey === 'project_id' ? sortDir : 'asc'} onClick={() => handleSort('project_id')} {...getSortLabelProps(sortKey === 'project_id')}>
                  Projekt
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 180 }}>
                <TableSortLabel active={sortKey === 'contractor_name'} direction={sortKey === 'contractor_name' ? sortDir : 'asc'} onClick={() => handleSort('contractor_name')} {...getSortLabelProps(sortKey === 'contractor_name')}>
                  Kontrahent
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 130 }}>
                <TableSortLabel active={sortKey === 'document_number'} direction={sortKey === 'document_number' ? sortDir : 'asc'} onClick={() => handleSort('document_number')} {...getSortLabelProps(sortKey === 'document_number')}>
                  Nr dokumentu
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 130 }} align="right">
                <TableSortLabel active={sortKey === 'gross_amount'} direction={sortKey === 'gross_amount' ? sortDir : 'asc'} onClick={() => handleSort('gross_amount')} {...getSortLabelProps(sortKey === 'gross_amount')}>
                  Wartość
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }}>
                <TableSortLabel active={sortKey === 'due_date'} direction={sortKey === 'due_date' ? sortDir : 'asc'} onClick={() => handleSort('due_date')} {...getSortLabelProps(sortKey === 'due_date')}>
                  Termin płatności
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 80 }}>
                <TableSortLabel active={sortKey === 'paid'} direction={sortKey === 'paid' ? sortDir : 'asc'} onClick={() => handleSort('paid')} {...getSortLabelProps(sortKey === 'paid')}>
                  Opłacono
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 140 }} aria-label="Zatwierdzenia" />
              <TableCell sx={{ ...headerCellSx, width: 50 }} align="center" aria-label="PDF" />
              <TableCell sx={{ ...headerCellSx, width: 40, borderRight: 'none' }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, color: '#B0B0B0', fontFamily: 'Roboto, sans-serif' }}>Brak wydatków</TableCell></TableRow>
            ) : sortedRows.map((row) => {
              const requesterName = row.created_by_name || '';
              const uObj = Object.values(usersMap).find(u => u.display_name === requesterName);
              const initial = requesterName ? requesterName.substring(0, 1).toUpperCase() : '?';
              const effectiveStatus = getEffectiveStatus(row);
              const statusDate: string = String(row.updated_at || row.accounting_approval_at || row.admin_approval_at || row.created_at || '');
              const dateLabel = statusDate ? ` • ${statusDate.slice(0, 16).replace('T', ' ')}` : '';
              const statusTip = `${effectiveStatus}${dateLabel}`;

              const accPending = !row.accounting_approval_status || String(row.accounting_approval_status) === 'pending';
              const accTitle = accPending ? 'Księgowość: oczekuje' : `Księgowość: ${row.accounting_approval_status}`;
              const accLetter = 'K';
              const accUser = row.accounting_approver_user_id ? usersMap[row.accounting_approver_user_id] : undefined;

              const admPending = !row.admin_approval_status || String(row.admin_approval_status) === 'pending';
              const admTitle = admPending ? 'Admin: oczekuje' : `Admin: ${row.admin_approval_status}`;
              const admLetter = 'A';
              const admUser = row.admin_approver_user_id ? usersMap[row.admin_approver_user_id] : undefined;

              return (
                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>
                    {row.expense_date ? new Date(row.expense_date).toLocaleDateString('pl-PL') : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`Wnioskuje: ${requesterName}`} arrow>
                      <Avatar src={uObj?.avatar_url || undefined} sx={{ width: 26, height: 26, fontSize: 11, bgcolor: '#E6E6EC' }}>
                        {initial}
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.project_id || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.contractor_name || row.description || '—'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.document_number || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{currency(Number(row.gross_amount) || 0)}</TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>
                    {row.due_date ? new Date(row.due_date).toLocaleDateString('pl-PL') : '—'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>
                    {row.paid ? 'Tak' : 'Nie'}
                  </TableCell>
                  {/* Zatwierdzenia */}
                  <TableCell sx={{ p: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={accTitle} arrow>
                          <Avatar src={accUser?.avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12, bgcolor: accPending ? '#e0e0e0' : undefined }}>
                            {accPending ? accLetter : String(accUser?.display_name || '?').substring(0, 1)}
                          </Avatar>
                        </Tooltip>
                        <Tooltip title={admTitle} arrow>
                          <Avatar src={admUser?.avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12, bgcolor: admPending ? '#e0e0e0' : undefined }}>
                            {admPending ? admLetter : String(admUser?.display_name || '?').substring(0, 1)}
                          </Avatar>
                        </Tooltip>
                      </Box>
                      {(() => {
                        if (effectiveStatus === 'Zatwierdzone') return (
                          <Tooltip title={statusTip} arrow><CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /></Tooltip>
                        );
                        if (effectiveStatus === 'Odrzucone') return (
                          <Tooltip title={statusTip} arrow><DoNotDisturbOn sx={{ fontSize: 18, color: 'error.main' }} /></Tooltip>
                        );
                        if (row.priority === 'pilny') return (
                          <Tooltip title={statusTip} arrow><ErrorOutline sx={{ fontSize: 18, color: '#D32F2F' }} /></Tooltip>
                        );
                        return (
                          <Tooltip title={statusTip || 'Do akceptacji'} arrow><InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} /></Tooltip>
                        );
                      })()}
                      {row.google_drive_file_url ? (
                        <Tooltip title="Skopiuj link do PDF" arrow>
                          <IconButton size="small" onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await navigator.clipboard.writeText(String(row.google_drive_file_url));
                              showNotification('Link do PDF skopiowany do schowka', 'success');
                            } catch {
                              showNotification('Nie udało się skopiować linku', 'error');
                            }
                          }}>
                            <ContentCopy fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                  {/* Podgląd PDF */}
                  <TableCell align="center">
                    <IconButton size="small" color="primary" title="Podgląd PDF" onClick={(e) => {
                      e.stopPropagation();
                      setPreviewExpense(row);
                      setPreviewOpen(true);
                    }}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                  {/* Kebab menu */}
                  <TableCell align="right" sx={{ position: 'relative' }}>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      setMenuRow(row);
                      setMenuPosition({ top: e.clientY, left: e.clientX });
                    }}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Wiersz podsumowania */}
            <TableRow data-sum-row sx={{ position: 'sticky', bottom: 0, backgroundColor: '#F1F1F6', zIndex: 1, '& .MuiTableCell-root': { borderBottom: 'none' } }}>
              <TableCell colSpan={5} align="right" sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>
                Suma:
              </TableCell>
              <TableCell align="right" sx={{ p: '6px 12px', height: 32, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#020242' }}>{currency(totals.gross)}</TableCell>
              <TableCell colSpan={5} sx={{ backgroundColor: '#F1F1F6' }} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Kebab menu */}
      {renderKebabMenu()}

      {/* Podgląd PDF – stream przez API (unika błędu uprawnień Google Drive) */}
      {previewOpen && previewExpense && (
        <Dialog open={previewOpen} onClose={() => { setPreviewOpen(false); setPreviewExpense(null); setPreviewUrl(null); setPreviewError(null); }} maxWidth="md" fullWidth>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '80vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 16 }}>
                Podgląd PDF – {previewExpense.document_number || previewExpense.description || 'Wydatek'}
              </Typography>
              <Button size="small" onClick={() => { setPreviewOpen(false); setPreviewExpense(null); setPreviewUrl(null); setPreviewError(null); }}>Zamknij</Button>
            </Box>
            {!previewExpense.google_drive_file_id ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B0B0B0' }}>
                <Typography>Brak pliku PDF</Typography>
              </Box>
            ) : previewLoading ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : previewError ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f' }}>
                <Typography>{previewError}</Typography>
              </Box>
            ) : previewUrl ? (
              <iframe
                title="pdf-preview"
                src={previewUrl}
                style={{ flex: 1, border: 'none', borderRadius: 4 }}
              />
            ) : null}
          </Box>
        </Dialog>
      )}

      {/* Dialog dodawania wydatku */}
      <AddOtherExpenseDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={() => refresh()}
      />
      {/* Dialog edycji wydatku (otwierany z menu "Edycja") */}
      <AddOtherExpenseDialog
        open={Boolean(editExpense)}
        onClose={() => setEditExpense(null)}
        onSave={() => { refresh(); setEditExpense(null); }}
        initialData={editExpense ?? undefined}
      />
    </Box>
  );
};

export default OtherExpensesPage;
